import { AfterViewInit, Directive, ElementRef, Input, OnChanges, Renderer2 } from '@angular/core';

import { Enclosure, PositionSide } from '../../prisma-xs/models/prisma-xs.model';
import { IUprightOnGrid, UprightOrientation } from '../../prisma-xs/models/upright.model';
import { PrismaXsConstant } from '../../shared/models/prisma-xs.constant';
import { IElementOnGrid } from '../../prisma-xs/models/grid-elements.model';
import { generateUprightReference } from '../../prisma-xs/helpers/prisma-xs.helper';

@Directive({
    selector: '[appUprights]',
    standalone: false
})
export class UprightsDirective implements AfterViewInit, OnChanges {
  @Input() upright: IUprightOnGrid;
  @Input() enclosure: Enclosure;
  @Input() zoomLevel: number;
  @Input() parentDiv: HTMLElement;

  constructor(private el: ElementRef, private renderer: Renderer2) {
  }

  ngAfterViewInit(): void {
    const parentRectangleDimensions = this.calculateSize(
      this.parentDiv, this.enclosure.configurationRows, this.enclosure.configurationFields);
    this.addUprights(this.upright, parentRectangleDimensions);
    this.addUprightsCommonStyle();
  }

  addUprightsCommonStyle() {
    this.renderer.setStyle(this.el.nativeElement, 'background-size', 'contain');
    this.renderer.setStyle(this.el.nativeElement, 'position', 'absolute');
    this.renderer.setStyle(this.el.nativeElement, 'z-index', `0`);
  }

  addVerticalUprightsStyle(height: number) {
    const imageName = generateUprightReference(height, true);
    this.renderer.setStyle(this.el.nativeElement, 'background', 'url("/assets/prisma-xs/upright-background/' + imageName + '")');
    this.renderer.setStyle(this.el.nativeElement, 'background-repeat', 'no-repeat');
  }

  addHorizontalUprightStyle(width: number) {
    const imageName = generateUprightReference(width, false);
    this.renderer.setStyle(this.el.nativeElement, 'background', 'url("/assets/prisma-xs/upright-background/' + imageName + '")');
    this.renderer.setStyle(this.el.nativeElement, 'background-repeat', 'no-repeat');
  }

  addUprights(upright: IUprightOnGrid, parentRectangleDimensions: IElementOnGrid) {
    switch (upright.orientation) {
      case UprightOrientation.VERTICAL:
        this.addVerticalUprightsStyle(upright?.dimensions?.height);
        this.verticalUpright(upright, parentRectangleDimensions);
        break;
      case UprightOrientation.HORIZONTAL:
        this.addHorizontalUprightStyle(upright?.dimensions?.width);
        this.addHorizontalUpright(upright, parentRectangleDimensions);
        break;
    }
  }

  verticalUpright(upright: IUprightOnGrid, parentRectangleDimensions: IElementOnGrid) {
    const uprightWidth = PrismaXsConstant.UPRIGHT_WIDTH;
    const uprightOffset = upright.positionSide === PositionSide.RIGHT ? 0 : uprightWidth;

    this.renderer.setStyle(this.el.nativeElement, 'left',
      `${(upright.placePosition.left - 1) * parentRectangleDimensions.widthOfOne - uprightOffset}px`);
    this.renderer.setStyle(this.el.nativeElement, 'top',
      `${(upright.placePosition.top - 1) * parentRectangleDimensions.heightOfOne}px`);
    this.renderer.setStyle(this.el.nativeElement, 'width', `${uprightWidth}px`);
    this.renderer.setStyle(this.el.nativeElement, 'height',
      `${parentRectangleDimensions.heightOfOne * upright.dimensions.height}px`);

    if (upright.positionSide === PositionSide.LEFT) {
      this.renderer.setStyle(this.el.nativeElement, 'background-position', `right`);
    }
  }

  addHorizontalUpright(upright: IUprightOnGrid, parentRectangleDimensions: IElementOnGrid) {
    const uprightWidth = PrismaXsConstant.UPRIGHT_WIDTH;
    const uprightOffset = upright.positionSide === PositionSide.UP ? uprightWidth : 0;

    this.renderer.setStyle(this.el.nativeElement, 'left',
      `${(upright.placePosition.left - 1) * parentRectangleDimensions.widthOfOne}px`);
    this.renderer.setStyle(this.el.nativeElement, 'top',
      `${(upright.placePosition.top - 1) * parentRectangleDimensions.heightOfOne - uprightOffset}px`);
    this.renderer.setStyle(this.el.nativeElement, 'height', `${uprightWidth}px`);
    this.renderer.setStyle(this.el.nativeElement, 'width',
      `${parentRectangleDimensions.widthOfOne * upright.dimensions.width}px`);

    const position = (upright.positionSide === PositionSide.DOWN) ? 'top' : 'bottom';
    this.renderer.setStyle(this.el.nativeElement, 'background-position', position);
  }

  calculateSize(element: HTMLElement, rows: number, cols: number): IElementOnGrid {
    const width = element.clientWidth;
    const height = element.clientHeight;
    const heightOfOne = height / rows;
    const widthOfOne = width / cols;

    return { height, widthOfOne, heightOfOne };
  }

  ngOnChanges(): void {
    const parentRectangleDimensions = this.calculateSize(
      this.parentDiv, this.enclosure.configurationRows, this.enclosure.configurationFields);
    this.addUprights(this.upright, parentRectangleDimensions);
    this.addUprightsCommonStyle();
  }
}
